import { useEffect, useState } from 'react';

/**
 * Données liées aux lignes affichées (managers des équipes, dates de clôture des périodes…), en une requête bornée par
 * la taille de la page : load(ids) renvoie [id de la ligne => donnée]. null pendant le chargement ; changer de lignes
 * relance la requête (la précédente est ignorée). Une erreur donne une table vide : les lignes restent affichées sans
 * la donnée. load null : prérequis pas encore là (liste de référence…) ; load doit être stable (useCallback).
 * version : la changer relance la requête pour les mêmes lignes (après une action qui modifie ces données).
 */
export function useRelatedByIds<V>(
  ids: (id | number)[],
  load: ((ids: string[]) => Promise<Map<number, V>>) | null,
  version = 0,
): Map<number, V> | null {
  const idList = ids.join(',');
  const key = idList && `${version}|${idList}`;
  const [state, setState] = useState<{ key: string; related: Map<number, V> } | null>(null);

  useEffect(() => {
    if (!key || !load) return;
    let cancelled = false;
    load(key.slice(key.indexOf('|') + 1).split(','))
      .then((related) => !cancelled && setState({ key, related }))
      .catch(() => !cancelled && setState({ key, related: new Map() }));

    return () => {
      cancelled = true;
    };
  }, [key, load]);

  if (!key) return new Map();

  return state?.key === key ? state.related : null;
}
